import FaqWrapper from "react-faq-component";

import { GenericExternalLink } from "@components/generic-external-link";

import { UtmChangeImpactTable } from "./utms/components/utm-change-impact";

const PlatformFAQContent = [
  {
    title:
      "Why do some platforms have a native integration in addition to UTMs?",
    content: `Platforms with native integrations pull ad spend and information directly from the API connection.
       Performance metrics still come from UTMs. Northbeam then maps ad performance from UTMs to spend pulled from the API connection.`,
  },
  {
    title: "Why do some platforms only require UTMs?",
    content:
      "Northbeam does not have native integrations for all platforms, but can pull ad performance metrics using UTMs.",
  },
  {
    title: "What is a UTM?",
    content: `UTM stands for Urchin Tracking Module -a term from a tool
    predating Google Analytics. A UTM is a set of parameters added to
    a URL to track web traffic stemming from a specific source (social
    ad, email, display ad, etc.).`,
  },
  {
    title: "Why do I need to configure UTMs?",
    content: (
      <>
        <p>
          Northbeam uses UTMs to attribute your metrics to specific ads -and
          create detailed customer journeys. UTMs give us the necessary data to
          accurately attribute each individual platform.
        </p>
        <div className="px-4 py-6 bg-sun-2 rounded border border-neutral-300 justify-start items-center gap-4 inline-flex w-full text-lg">
          <i className="fa-sharp fa-regular fa-triangle-exclamation font-black text-[#FFB300]" />
          <strong>
            Failing to configure UTMs for a platform will significantly limit
            Northbeam’s reporting capabilities.
          </strong>
        </div>
      </>
    ),
  },
  {
    title: "What if my platform is not listed?",
    content: (
      <p>
        If a platform is not listed, you can track it by following{" "}
        <GenericExternalLink href="https://docs.northbeam.io/docs/tracking-for-other-platforms">
          Generic UTM guide
        </GenericExternalLink>
        . You will need to add your spend data for these platforms through our
        Spend API in the next step.
      </p>
    ),
  },
  {
    title: "Will Northbeam UTMs impact my existing UTM configuration?",
    content: (
      <>
        <p>
          Our UTMs are all <i>Additive</i>, meaning they can be appended to your
          existing UTMs and will not impact your performance or tracking.
        </p>
        <p>
          The order of UTMs does not matter, meaning you can add our UTMs to the
          beginning or the end of your existing UTM parameters (just make sure
          there is an “&” separating them).
        </p>
      </>
    ),
  },
  {
    title: "How will updating UTMs effect existing ad tracking?",
    content: (
      <>
        <p>
          In certain channels (ex. Meta Ads), any ad changes, including editing
          UTMs, may reset the ad to the Learning Phase, which could impact
          performance. We recommend ‘ripping off the bandaid’ and updating all
          ads at once – this way, you can start using our data immediately.
          However, it’s completely up to you. You could wait for a low-traffic
          period to make changes, or wait until the next creative refresh, for
          example.{" "}
          <strong>
            But please know that Northbeam will not be fully tracking ads
            missing our required UTMs.
          </strong>
        </p>
        <div>
          <p className="text-lg font-semibold font-['TT Commons'] leading-normal">
            Here’s how different platforms are impacted by UTM updates:
          </p>
          <UtmChangeImpactTable />
        </div>
      </>
    ),
  },
];

const styles = {
  bgColor: "white",
  rowTitleTextSize: "20px",
  rowTitleColor: "#021330", // interstellar
  rowContentTextSize: "16px",
  rowContentPaddingTop: "10px",
  rowContentPaddingBottom: "10px",
  rowContentPaddingLeft: "20px",
  rowContentPaddingRight: "20px",
  arrowColor: "black",
};

const config = {
  tabFocus: true,
};

export const FAQSection = (): JSX.Element => (
  <FaqWrapper
    data={{ rows: PlatformFAQContent }}
    styles={styles}
    config={config}
  />
);
